import { ajvResolver } from '@hookform/resolvers/ajv';
import { yupResolver } from '@hookform/resolvers/yup';
import { GameEntity, PlatformType } from '@okletsplay/core';
import { MatchAccessibility } from '@okletsplay/core/src/modules/match/enums';
import { merge } from 'lodash';
import { useDynamicSchemaForMatchFromGame } from 'modules/games/hooks/use-dynamic-schema-from-game';
import { MatchFormFields } from 'modules/matches/interfaces/forms';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { MATCH_PLAYERS_DEFAULT_SETTINGS } from '../../constants';
import {
  CreateMatchValidationOptions,
  getCreateMatchValidationSchema,
  lichessMatchTimeValidationSchema,
  lidraughtsMatchTimeValidationSchema,
} from './create-match-validation-schema';

const DEFAULT_VALUES = {
  gameId: '',
  accessibility: MatchAccessibility.Public,
  isJoinApproveRequired: false,
  entryFee: 0,
  players: [],
  matchSetupSettings: {},
  playersSettings: MATCH_PLAYERS_DEFAULT_SETTINGS,
};

export const useCreateMatchForm = (
  game: GameEntity | undefined,
  createMatchOptions: CreateMatchValidationOptions
) => {
  const staticSchema = useMemo(() => {
    const defaultSchema = getCreateMatchValidationSchema(createMatchOptions);
    switch (game?.platformType) {
      case PlatformType.Lichess:
        return defaultSchema.concat(lichessMatchTimeValidationSchema);
      case PlatformType.Lidraughts:
        return defaultSchema.concat(lidraughtsMatchTimeValidationSchema);
      default:
        return defaultSchema;
    }
  }, [game, createMatchOptions]);

  const defaultValues = useMemo<MatchFormFields>(() => {
    switch (game?.platformType) {
      case PlatformType.Lichess:
      case PlatformType.Lidraughts:
        return {
          ...DEFAULT_VALUES,
          clockLimit: 180,
          clockIncrement: 2,
        };
      default:
        return DEFAULT_VALUES;
    }
  }, [game?.platformType]);

  const dynamicSchema = useDynamicSchemaForMatchFromGame(game);

  const form = useForm<MatchFormFields>({
    mode: 'onBlur',
    resolver: async (fields, context, options) => {
      const yup = await yupResolver(staticSchema)(fields, context, options);

      if (!dynamicSchema) {
        return yup;
      }

      const ajv = await ajvResolver(dynamicSchema, { strict: false })(fields, context, options);
      return merge(yup, ajv);
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    defaultValues,
  });

  return {
    form,
    defaultValues,
  };
};
